"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.roleCapabilities = void 0;
exports.roleHasCapability = roleHasCapability;
exports.getRoleCapabilities = getRoleCapabilities;
/**
 * Capability groups for composable role definitions
 * These groups define common sets of capabilities that can be reused across roles
 */
// Dashboard & Analytics capabilities
const DASHBOARD_CAPABILITIES = ['can_access_dashboard'];
// Accounting & Financial capabilities
const ACCOUNTING_CAPABILITIES = [
    'can_access_accounting',
    'can_access_attending_accounting',
    'can_generate_accounting',
    'can_import_accounting_files',
];
// Accounting - Read only (view, no generate)
const ACCOUNTING_READ_CAPABILITIES = ['can_access_accounting'];
// User Management capabilities
const USER_MANAGEMENT_CAPABILITIES = ['can_manage_users'];
// Medical Assistants Management capabilities (admin + support only)
const ASSISTANTS_MANAGEMENT_CAPABILITIES = ['can_manage_assistants'];
// Medical Assistants Planning - Read only (admin, support, medical_assistant)
const ASSISTANTS_PLANNING_READ_CAPABILITIES = [
    'can_view_assistants_planning',
];
// Sites - Read and Write only (no create/delete)
const SITE_READ_WRITE = ['can_view_sites', 'can_edit_sites'];
// Sites - Create and Delete only (for admin/support who have full site management)
const SITE_CREATE_DELETE = ['can_create_sites', 'can_delete_sites'];
// Offices - Full CRUD
const OFFICE_CRUD = [
    'can_view_offices',
    'can_edit_offices',
    'can_create_offices',
    'can_delete_offices',
];
// Doctors - Full CRUD
const DOCTOR_CRUD = [
    'can_view_doctors',
    'can_edit_doctors',
    'can_create_doctors',
    'can_delete_doctors',
];
// Doctor Menu Access - Controls menu visibility (separate from data access)
const DOCTOR_MENU_ACCESS = [
    'can_access_doctor_substitutes_menu',
    'can_access_doctor_attendings_menu',
];
// Offices Menu Access - Controls menu visibility (separate from data access)
const OFFICES_MENU_ACCESS = ['can_access_offices_menu'];
// Agenda Slots - Full CRUD
const SLOT_CRUD = [
    'can_view_slots',
    'can_edit_slots',
    'can_create_slots',
    'can_delete_slots',
];
// Reservations - Full CRUD
const RESERVATION_CRUD = [
    'can_view_reservations',
    'can_edit_reservations',
    'can_create_reservations',
    'can_delete_reservations',
];
// Reservations - Read only (substitute doctor level)
const RESERVATION_READ = ['can_view_reservations'];
// Invitations - Full access
const INVITATION_CAPABILITIES = [
    'can_view_invitations',
    'can_manage_invitations',
];
// Work Schedule - Full access
const WORK_SCHEDULE_CAPABILITIES = [
    'can_view_work_schedules',
    'can_edit_work_schedules',
];
// Contracts - Admin level (can view all contracts)
const CONTRACT_ADMIN_CAPABILITIES = ['can_view_all_contracts'];
// Contracts - Substitute doctor level (can view own contracts only)
const CONTRACT_OWN_CAPABILITIES = ['can_view_own_contracts'];
// Referral programme - practising doctors of either kind
const REFERRAL_PROGRAM_CAPABILITIES = ['can_use_referral_program'];
// Installation offer ("Nous rejoindre") - substitutes only; an attending has
// already installed, so the pitch does not apply to them
const RECRUITMENT_OFFER_CAPABILITIES = ['can_view_recruitment_offer'];
// Prospect mirror - internal staff reading the commercial pipeline
const PROSPECT_CAPABILITIES = ['can_view_prospects'];
// Shared documents - tracking and management, held by admin and support. Kept
// split so tracking can still be opened to csm without granting publication.
const DOCUMENT_ADMIN_CAPABILITIES = [
    'can_view_documents',
    'can_manage_documents',
];
// Shared documents - reading space of the document audiences (substitute,
// attending, medical assistant). Never office_assistant.
const DOCUMENT_READ_CAPABILITIES = ['can_read_documents'];
// Rhythm Notes - Edit capability for unstructured rhythm notes (viewing is unrestricted)
const RHYTHM_NOTES_CAPABILITIES = ['can_edit_rhythm_notes'];
// Office Planning Tools - Access to schedule generation and occupation reports
const OFFICE_PLANNING_TOOLS_CAPABILITIES = [
    'can_access_office_planning_tools',
];
// Financial fields - Edit access to sensitive doctor/office financial data
// Admin only: intentionally NOT part of STAFF_CAPABILITIES and NOT granted to
// support or csm
const FINANCIAL_EDIT_CAPABILITIES = [
    'can_edit_doctor_financials',
    'can_edit_office_financials',
];
// Staff capabilities - shared by admin, support, and csm roles
// These are the core operational capabilities for internal staff
const STAFF_CAPABILITIES = [
    // Menu access
    ...DOCTOR_MENU_ACCESS,
    ...OFFICES_MENU_ACCESS,
    // Sites - view and edit (create/delete varies by role)
    ...SITE_READ_WRITE,
    // Offices - full CRUD
    ...OFFICE_CRUD,
    // Doctors - full CRUD
    ...DOCTOR_CRUD,
    // Slots - full CRUD
    ...SLOT_CRUD,
    // Reservations - full CRUD
    ...RESERVATION_CRUD,
    // Work schedules
    ...WORK_SCHEDULE_CAPABILITIES,
    // Invitations
    ...INVITATION_CAPABILITIES,
    // Rhythm notes
    ...RHYTHM_NOTES_CAPABILITIES,
    // Office planning tools
    ...OFFICE_PLANNING_TOOLS_CAPABILITIES,
    // Prospect pipeline mirrored from Airtable
    ...PROSPECT_CAPABILITIES,
];
// API Partner - Read-only access to doctors, slots, work schedules, and offices
const API_PARTNER_CAPABILITIES = [
    'can_view_doctors',
    'can_view_offices',
    'can_view_slots',
    'can_view_work_schedules',
];
// Medical Assistant - Read-only access to assigned offices' agendas
const AGENDA_READ_CAPABILITIES = [
    'can_view_offices',
    'can_view_slots',
    'can_view_work_schedules',
];
// Attending Doctor - Access to own offices (via DoctorAttending relationship)
// Resource filtering is handled at use-case level
const ATTENDING_DOCTOR_CAPABILITIES = [
    'can_access_dashboard',
    'can_access_accounting',
    'can_access_attending_accounting',
    'can_view_offices',
    'can_view_slots',
    'can_view_work_schedules',
    'can_create_own_work_schedules',
    'can_view_reservations',
    'can_view_own_contracts',
    'can_view_doctors',
    // Read assistant presence on their own offices' agenda slots (card + tooltip).
    // Does NOT grant the assistant planning/absences pages (those use
    // can_view_assistants_planning).
    'can_view_office_assistant_presence',
    ...REFERRAL_PROGRAM_CAPABILITIES,
    ...DOCUMENT_READ_CAPABILITIES,
];
/**
 * Role capability mapping
 * Defines which capabilities each role has using composable groups
 */
exports.roleCapabilities = {
    /**
     * Admin - Full system access
     */
    admin: [
        ...STAFF_CAPABILITIES,
        ...SITE_CREATE_DELETE,
        ...DASHBOARD_CAPABILITIES,
        ...ACCOUNTING_CAPABILITIES,
        ...USER_MANAGEMENT_CAPABILITIES,
        ...ASSISTANTS_MANAGEMENT_CAPABILITIES,
        ...ASSISTANTS_PLANNING_READ_CAPABILITIES,
        ...CONTRACT_ADMIN_CAPABILITIES,
        ...RHYTHM_NOTES_CAPABILITIES,
        ...FINANCIAL_EDIT_CAPABILITIES,
        ...DOCUMENT_ADMIN_CAPABILITIES,
    ],
    /**
     * Support - Admin access except Accounting and User Management
     */
    support: [
        ...STAFF_CAPABILITIES,
        ...SITE_CREATE_DELETE,
        ...DASHBOARD_CAPABILITIES,
        ...ASSISTANTS_MANAGEMENT_CAPABILITIES,
        ...ASSISTANTS_PLANNING_READ_CAPABILITIES,
        'can_view_all_contracts',
        'can_view_own_contracts',
        'can_import_accounting_files',
        ...DOCUMENT_ADMIN_CAPABILITIES,
    ],
    /**
     * CSM (Customer Success Manager) - Similar to admin but restricted to assigned sites
     * Note: Site-level restrictions are enforced at the permission level, not capability level
     */
    csm: [
        ...STAFF_CAPABILITIES,
        ...DASHBOARD_CAPABILITIES,
        ...ACCOUNTING_CAPABILITIES,
        ...USER_MANAGEMENT_CAPABILITIES,
        ...CONTRACT_ADMIN_CAPABILITIES,
    ],
    /**
     * Substitute Doctor - Limited read-only access
     */
    substitute_doctor: [
        ...RESERVATION_READ,
        ...AGENDA_READ_CAPABILITIES,
        ...CONTRACT_OWN_CAPABILITIES,
        ...ACCOUNTING_READ_CAPABILITIES,
        ...DASHBOARD_CAPABILITIES,
        ...REFERRAL_PROGRAM_CAPABILITIES,
        ...RECRUITMENT_OFFER_CAPABILITIES,
        ...DOCUMENT_READ_CAPABILITIES,
        'can_view_doctors',
    ],
    /**
     * API Partner - Read-only API access for external integrations
     * Used by external partners (e.g., Doctolib) for programmatic access
     * Authenticates via bearer token instead of JWT
     */
    api_partner: [...API_PARTNER_CAPABILITIES],
    /**
     * Attending Doctor - Read-only access to their associated offices
     * Access is determined via DoctorAttending relationship (doctor linked to offices)
     * Similar to substitute_doctor but office access is via different mechanism
     */
    attending_doctor: [...ATTENDING_DOCTOR_CAPABILITIES],
    /**
     * Medical Assistant - Read-only access to agenda views for assigned offices
     * Note: Office-level restrictions are enforced at the permission level via UserOfficeAssignment
     */
    medical_assistant: [
        ...AGENDA_READ_CAPABILITIES,
        ...ASSISTANTS_PLANNING_READ_CAPABILITIES,
        ...DOCUMENT_READ_CAPABILITIES,
    ],
    /**
     * Office Assistant - Legacy assistant accounts (no MedicalAssistantProfile).
     * Read-only access to the agenda views (Créneaux) for their assigned offices,
     * matching what the original medical assistant accounts could do before the
     * HR planning feature. Excludes the assistants-planning grid/absences, which
     * require a MedicalAssistantProfile these accounts do not have.
     * Office-level restrictions are enforced via UserOfficeAssignment.
     */
    office_assistant: [...AGENDA_READ_CAPABILITIES],
};
/**
 * Check if a role has a specific capability
 */
function roleHasCapability(role, capability) {
    return exports.roleCapabilities[role]?.includes(capability) ?? false;
}
/**
 * Get all capabilities for a role
 */
function getRoleCapabilities(role) {
    return exports.roleCapabilities[role] ?? [];
}
